import { Box, IconButton, Typography, CircularProgress, Tooltip } from '@mui/material'
import React, { useState } from 'react'
import { PartnerPageData } from 'types/types'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { useGetAllMiniLeagues } from 'hooks/useGetAllMiniLeagues'
import { useDebounce } from 'hooks/useDebounce'
import JoinLeagueItem from '@/../components/JoinLeagueItem'
import { useRouter } from 'next/router'
import { BASE_API_URL } from '@/configs'
import axios from 'axios'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { getPartnerIdentifier } from '@/utils/getPartnerIdentifier'
import { getStartSportsWording } from '@/lib/feature-flags/potm'
import {
  getStarSportsColor,
  getStarSportsFont,
  getStarSportsFontSize,
  getStarSportsFontStyle,
} from '@/utils/starSportsUtils'

function JoinLeague({ page }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [pin, setPin] = useState<string>('')
  const debouncedPin = useDebounce(pin, 500)
  const { data, isLoading } = useGetAllMiniLeagues(page.game_id ?? 0, debouncedPin)
  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value)
  }
  const router = useRouter()

  const onClose = () => {
    router.back()
  }

  const handleOpenModal: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      setPin(pin)
    }
  }

  return (
    <Box
      width="100vw"
      height="100vh"
      sx={{
        backgroundColor: 'rgba(245, 245, 245, 1)',
        overflowY: 'scroll',
        padding: 2,
        paddingTop: '50px',
      }}
    >
      <Box
        sx={{ position: 'absolute', left: '25px', top: '65px' }}
        flexDirection="row"
        display="flex"
        justifyContent="flex-start"
      >
        <IconButton onClick={onClose} sx={{ width: 14, height: 14, padding: 0 }}>
          <ArrowBackIosIcon sx={{ width: 14, height: 14 }} />
        </IconButton>
      </Box>
      <Box display="flex" justifyContent="center">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
          maxWidth="700px"
          flexDirection="column"
          height="100%"
        >
          <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
            <Typography
              fontWeight="900"
              fontStyle={getStarSportsFontStyle(page)}
              textAlign="center"
              fontSize={getStarSportsFontSize(page, 24)}
              color={getStarSportsColor(page)}
              fontFamily={getStarSportsFont(page)}
            >
              JOIN GAME WITH PIN
            </Typography>

            <Typography color="#353535" textAlign="center" my={2} fontSize={14}>
              {getStartSportsWording(page).JOIN_YOUR_MATES_LEAGUE_AND_COMPETE_TO_WIN}
            </Typography>
          </Box>
          <Box style={{ position: 'relative', width: '100%' }}>
            <input
              style={{
                width: '100%',
                height: '50px',
                borderRadius: '8px',
                border: '1px solid #D4D4D4',
                fontFamily: 'DM Sans',
                fontSize: '15px',
                padding: '18px 40px 18px 18px',
              }}
              onKeyDown={handleOpenModal}
              placeholder="Enter PIN number"
              onChange={handlePinChange}
              value={pin}
            />
          </Box>

          <Tooltip
            enterTouchDelay={0}
            title="This will allow you to quickly join a Private League if you have received a unique PIN code."
            placement="bottom"
          >
            <Typography
              sx={{
                mt: 2,
                width: '165px',
                fontWeight: '500',
                textDecoration: 'underline',
                textAlign: 'center',
                color: 'rgba(53, 53, 53, 0.6)',
              }}
            >
              Not sure what this is?
            </Typography>
          </Tooltip>
          <Box mt={3} sx={{ width: '100%' }}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              data?.private.map((league) => (
                <JoinLeagueItem key={league.id} isPublic pageData={page} league={league} />
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default JoinLeague

export async function getServerSideProps(context: GetServerSidePropsContext<{ slug: string }>) {
  const { slug, identifier, redirect } = getPartnerIdentifier(context)
  if (redirect) {
    return { redirect }
  }
  const { game_id } = context.query
  const res = await axios.get<PartnerPageData>(`${BASE_API_URL}/b2b/partner-pages/${identifier}`)
  return {
    props: {
      page: {
        ...res.data,
        game_id: game_id ? Number(game_id) : 0,
      },
    },
  }
}
